import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Globe, 
  FileText, 
  TrendingUp, 
  Database
} from 'lucide-react';
import SparrowTuneIcon from '../components/icons/SparrowTuneIcon';

const services = [
  {
    icon: null, // Custom SparrowTune icon
    title: 'SparrowTune',
    tagline: 'The Music Business Heartbeat. Tune in with every headline, event, and opportunity.',
    description: 'Your essential companion for staying connected to the pulse of the music industry.',
    features: [
      'Real-Time Industry News: Stay ahead of the curve with breaking news and global market shifts delivered as they happen.',
      'Curated Global Job Board: Access a high-intent directory of career opportunities specifically for music tech, labels, and distribution executives.',
      'Strategic Event Aggregator: Never miss a key industry summit, global tour, or international music conference again.'
    ],
    externalLink: 'https://sparrowtune.app',
    isFeatured: true,
    isCustomIcon: true,
  },
  {
    icon: Globe,
    title: 'Distribution',
    description: 'Global music distribution services ensuring your content reaches every major platform and streaming service worldwide. We handle the logistics while you focus on creating.',
    features: ['Major Platform Access', 'Global Reach', 'Real-time Analytics', 'Revenue Optimization'],
  },
  {
    icon: FileText,
    title: 'Music Licensing',
    description: 'Comprehensive music licensing solutions for film, television, advertising, and digital media. We connect your music with opportunities.',
    features: ['Music Licensing'],
  },
  {
    icon: TrendingUp,
    title: 'Market Expansion',
    description: 'Strategic planning and execution for entering new markets, whether territorial expansion or genre diversification.',
    features: ['Market Analysis', 'Entry Strategy', 'Local Partnerships', 'Cultural Adaptation'],
  },
  {
    icon: Database,
    title: 'Catalog Acquisition',
    description: 'Expert guidance on music catalog acquisition, valuation, due diligence, and integration for investors and labels.',
    features: ['Valuation Services', 'Due Diligence', 'Deal Structuring', 'Integration Support'],
  },
];

const ServicesPage = () => {
  return (
    <div className="page-transition" data-testid="services-page">
      {/* Hero Section */}
      <section className="section-padding bg-richblack-light" data-testid="services-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <div className="gold-line mb-6" />
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              Our <span className="text-gold-gradient">Services</span>
            </h1>
            <p className="text-lg text-white/70 leading-relaxed">
              Comprehensive music industry consultancy services designed to elevate 
              your business. From distribution to catalog acquisition, we provide 
              end-to-end solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-richblack" data-testid="services-grid">
        <div className="container-custom">
          {/* Featured SparrowTune Card */}
          {services.filter(s => s.isFeatured).map((service) => (
            <div 
              key={service.title}
              className="mb-8 bg-richblack border border-gold/30 p-8 md:p-12"
              data-testid="service-sparrowtune"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_music-licensing-hub-2/artifacts/74g8hswa_SparrowTune%20HD.png" 
                    alt="SparrowTune Logo"
                    className="w-32 h-32 -mb-4 object-contain"
                    style={{ filter: 'sepia(100%) hue-rotate(-10deg) saturate(300%) brightness(90%)' }}
                  />
                  <h3 className="font-serif text-3xl text-white mb-2">{service.title}</h3>
                  <p className="text-gold italic mb-4">{service.tagline}</p>
                  <p className="text-white/60 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <a 
                    href={service.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Visit SparrowTune
                    <ArrowRight size={18} />
                  </a>
                </div>
                <div>
                  <h4 className="text-white font-sans font-semibold mb-4 text-sm tracking-wider uppercase">Key Features</h4>
                  <ul className="space-y-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex gap-3">
                        <span className="w-2 h-2 bg-gold rounded-full mt-2 flex-shrink-0" />
                        <span className="text-white/70 text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}

          {/* Other Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.filter(s => !s.isFeatured).map((service, index) => (
              <div 
                key={service.title}
                className="service-card group"
                data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div>
                  <service.icon 
                    className="text-gold mb-6 group-hover:scale-110 transition-transform duration-500" 
                    size={36} 
                    strokeWidth={1.5} 
                  />
                  <h3 className="font-serif text-xl text-white mb-3">{service.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                </div>
                <div>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature) => (
                      <li key={feature} className="text-white/40 text-xs flex items-center gap-2">
                        <span className="w-1 h-1 bg-gold rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-white/5">
                    <Link 
                      to="/contact" 
                      className="text-gold text-sm hover:text-gold-light inline-flex items-center gap-2 transition-colors"
                    >
                      Inquire
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-richblack-light" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="gold-line mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Our Process
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              A structured approach to delivering exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Understanding your goals, challenges, and market position' },
              { step: '02', title: 'Strategy', desc: 'Developing a tailored roadmap for success' },
              { step: '03', title: 'Execution', desc: 'Implementing solutions with precision and care' },
              { step: '04', title: 'Optimization', desc: 'Continuous improvement based on results and feedback' },
            ].map((item, index) => (
              <div key={item.step} className="text-center" data-testid={`process-step-${index}`}>
                <span className="text-5xl font-serif text-gold/30">{item.step}</span>
                <h3 className="font-serif text-xl text-white mt-4 mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-richblack" data-testid="services-cta">
        <div className="container-custom text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-10">
            Contact us to discuss how our services can help achieve your goals.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-flex items-center gap-2"
            data-testid="services-contact-btn"
          >
            Contact Us
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
